import config from 'config';
import { AuthState } from 'store/types';
import { clearExtensions } from '../store/reducers/menu';
import { clearAuth, setAuth } from '../store/reducers/auth';
import { ApiClass, ApiClassConstructor } from './types';

import { AuthApiClass } from './auth';

export default class ApiFactory {
    private readonly apiHost: string;

    private authApi: ApiClass<AuthApiClass>;

    private session: AuthState | undefined = undefined;

    private networkErrorCallable: () => void = () => {};

    private requestLocale: string = 'de-DE';

    private dispatch: (action: any) => void = () => {};

    private timeout?: NodeJS.Timeout;

    constructor(authApi: ApiClassConstructor<AuthApiClass>) {
        this.apiHost = config.apiHost;
        this.authApi = authApi(this);
        this.timeout = undefined;
    }

    public getApiHost = () => {
        return this.apiHost;
    };

    public getSession = () => {
        return this.session;
    };

    public updateSession = (session: AuthState) => {
        this.session = session;
        this.startNewRefreshTimeout();
    };

    public onNetworkError = () => {
        this.networkErrorCallable();
    };

    public onError = (response: Response) => {
        if (response.status === 401 && this.session?.isAuthenticated) {
            this.refreshToken();
        }
    };

    public getRequestLocale = () => {
        return this.requestLocale;
    };

    public updateRequestLocale = (locale: string) => {
        this.requestLocale = locale;
    };

    public updateDispatchFunction = (
        dispatchFunction: (action: any) => void,
    ) => {
        this.dispatch = dispatchFunction;
    };

    private refreshToken = async () => {
        let response: Response | null = null;
        const onFail = () => {
            this.dispatch(clearExtensions());
            this.dispatch(clearAuth());
        };
        try {
            response = await this.authApi.refreshToken();
        } catch (e) {
            onFail();
        }

        if (response && response?.status === 200) {
            const authData = await response.json();
            this.dispatch(setAuth(authData));
        } else {
            onFail();
        }
    };

    private startNewRefreshTimeout = () => {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        const REFRESH_INTERVAL = 60 * 30 * 1000; // 30 min
        this.timeout = setTimeout(this.refreshToken, REFRESH_INTERVAL);
    };
}

export type ApiFactoryClassType = ApiFactory;
